import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import {
    getDueClass,
    getInitials,
    formatTaskDate,
    getTaskInspectionNumericId,
    getTaskInspectionRouteId,
    mapDashboardTaskForActions,
    isTaskCompleted,
    SHOW_TASK_DASHBOARD_SCORE,
    getTaskCreatedByName,
    getTaskCustomerDisplay,
    getTaskTemplateName,
    DASHBOARD_TOP_ANSWER_LIMIT,
    flattenFullPreviewAnswers,
    getFullPreviewProgress,
    getFullPreviewScoreBySection,
} from './task-dashboard-utils'
import { TaskStatusBadge } from './TaskStatusBadge'
import { AuthService, CommonUtility, StatusType, isTaskReadOnly } from 'utility'
import { useNotification } from 'hooks/toast'
import { resolveResumeInspectionId } from 'hooks/inspection-task'
import { ViewOnlyChip } from './task-dashboard-styles'

const QuickPreview = styled.div`
    position: fixed;
    width: 360px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
    z-index: ${(p) => (p.$expanded ? 1100 : 900)};
    max-height: 75vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    opacity: ${(p) => (p.$open ? 1 : 0)};
    pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.12s, width 0.15s, max-height 0.15s;

    &.expanded {
        position: fixed !important;
        top: 20px !important;
        right: 20px !important;
        left: auto !important;
        width: 520px;
        max-height: calc(100vh - 40px);
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    &.expanded::before {
        display: none;
    }

    &::before {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        background: #fff;
        border: 1px solid #e5e7eb;
        transform: rotate(45deg);
        z-index: -1;
    }

    &.dir-right::before {
        left: -7px;
        top: 24px;
        border-right: 0;
        border-top: 0;
    }

    &.dir-left::before {
        right: -7px;
        top: 24px;
        border-left: 0;
        border-bottom: 0;
    }

    &.dir-down::before {
        top: -7px;
        left: 24px;
        border-bottom: 0;
        border-right: 0;
    }

    &.dir-up::before {
        bottom: -7px;
        left: 24px;
        border-top: 0;
        border-left: 0;
    }
`

const PreviewHeader = styled.header`
    padding: 14px 16px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    background: #fff;
    flex-shrink: 0;

    h2 {
        font-size: 14px;
        margin: 0 0 2px;
    }

    .sub {
        color: #6b7280;
        font-size: 12px;
    }
`

const HeaderActions = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
`

const SmallButton = styled.button`
    background: ${(p) => (p.$active ? '#2563eb' : '#f3f4f6')};
    color: ${(p) => (p.$active ? '#fff' : '#374151')};
    border: 1px solid ${(p) => (p.$active ? '#2563eb' : '#e5e7eb')};
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 11px;
    cursor: pointer;

    &.expand-active {
        background: #8b5cf6;
        color: #fff;
        border-color: #8b5cf6;
    }
`

const PreviewTop = styled.div`
    flex-shrink: 0;
    background: #fff;
`

const PreviewMetaSection = styled.section`
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
`

const PreviewScrollBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 16px;
`

const PreviewFooter = styled.footer`
    flex-shrink: 0;
    padding: 12px 16px;
    border-top: 1px solid #f3f4f6;
    background: #fff;
`

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: 90px 1fr;
    row-gap: 6px;
    column-gap: 12px;
    font-size: 12px;

    .k {
        color: #6b7280;
    }

    .v {
        color: #111827;
        font-weight: 500;
    }
`

const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    margin-bottom: 6px;
    letter-spacing: 0.03em;
`

const QaItem = styled.div`
    padding: 8px 10px;
    background: ${(p) => {
        if (p.$tag === 'nc') return '#fef2f2'
        if (p.$tag === 'warn') return '#fffbeb'
        if (p.$tag === 'ok') return '#ecfdf5'
        return '#f9fafb'
    }};
    border-radius: 6px;
    margin-bottom: 6px;
    border-left: 3px solid
        ${(p) => {
            if (p.$tag === 'nc') return '#ef4444'
            if (p.$tag === 'warn') return '#f59e0b'
            if (p.$tag === 'ok') return '#10b981'
            return '#e5e7eb'
        }};
    font-size: 12px;

    .q {
        color: #374151;
        font-weight: 500;
        margin-bottom: 3px;
    }

    .a b {
        background: #fff;
        padding: 1px 6px;
        border-radius: 3px;
        border: 1px solid #e5e7eb;
        font-weight: 600;
        font-size: 11px;
    }

    .note {
        color: #6b7280;
        font-style: italic;
        margin-top: 4px;
        font-size: 11px;
    }
`

const ScoreRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 12px;
    border-bottom: 1px dashed #e5e7eb;

    &:last-child {
        border-bottom: 0;
    }
`

const PreviewActions = styled.div`
    display: flex;
    gap: 6px;

    button {
        flex: 1;
        padding: 7px 10px;
        font-size: 12px;
        border: 1px solid #d1d5db;
        border-radius: 5px;
        background: #fff;
        cursor: pointer;
    }

    .primary {
        background: #2563eb;
        color: #fff;
        border-color: #2563eb;
    }
`

const ProgressRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    margin-top: 10px;

    .bar-wrap {
        flex: 1;
        height: 6px;
        background: #f3f4f6;
        border-radius: 3px;
        overflow: hidden;
    }

    .bar {
        height: 100%;
        background: linear-gradient(90deg, #2563eb, #06b6d4);
        width: ${(p) => p.$pct || 0}%;
    }
`

const Avatar = styled.span`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #c7d2fe;
    color: #1e3a8a;
    font-size: 9px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`

function renderAnswerItems(answers) {
    if (!answers?.length) {
        return (
            <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>
                No answers yet.
            </div>
        )
    }
    return answers.map((a, idx) => (
        <QaItem key={idx} $tag={a.Tag || a.tag}>
            <div className="q">
                {(a.SectionName || a.section) && (
                    <span style={{ color: '#6b7280', fontSize: 11 }}>
                        [{a.SectionName || a.section}]{' '}
                    </span>
                )}
                {CommonUtility.strippedHtml(a.Question || a.q || '')}
            </div>
            <div className="a">
                Answer: <b>{CommonUtility.strippedHtml(a.Answer || a.a || '—')}</b>
            </div>
            {(a.Note || a.note) && (
                <div className="note">
                    Note: {CommonUtility.strippedHtml(a.Note || a.note || '')}
                </div>
            )}
        </QaItem>
    ))
}

function renderScoreRows(scores) {
    if (!scores?.length) {
        return (
            <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}>
                No score yet.
            </div>
        )
    }
    return scores.map((s, idx) => {
        const pct = s.Pct ?? s.pct ?? 0
        let color = '#991b1b'
        if (pct >= 90) color = '#065f46'
        else if (pct >= 60) color = '#92400e'
        return (
            <ScoreRow key={idx}>
                <span>{s.SectionName || s.name}</span>
                <span style={{ fontWeight: 600, color }}>{pct}%</span>
            </ScoreRow>
        )
    })
}

export const TaskQuickPreview = ({
    task,
    anchorEl,
    open,
    onClose,
    onViewReport,
    pinned: pinnedProp,
    onPinnedChange,
    onHoverStart,
    onHoverEnd,
    fullPreview,
    fullPreviewLoading = false,
    fullPreviewError = false,
    reportApproved = false,
}) => {
    const navigate = useNavigate()
    const { errorToast } = useNotification()
    const qpRef = useRef(null)
    const [pinnedInternal, setPinnedInternal] = useState(false)
    const pinned = pinnedProp ?? pinnedInternal
    const setPinned = onPinnedChange ?? setPinnedInternal
    const [expanded, setExpanded] = useState(false)
    const [direction, setDirection] = useState('right')

    const positionPreview = useCallback(() => {
        if (!anchorEl || !qpRef.current || expanded) return
        const qp = qpRef.current
        const anchor = anchorEl.getBoundingClientRect()
        const gap = 12
        const pad = 8
        const qpW = qp.offsetWidth || 360
        const qpH = qp.getBoundingClientRect().height || qp.offsetHeight || 400
        const vw = window.innerWidth
        const vh = window.innerHeight

        const fitsRight = anchor.right + gap + qpW <= vw - pad
        const fitsLeft = anchor.left - gap - qpW >= pad
        const fitsBelow = anchor.bottom + gap + qpH <= vh - pad
        const fitsAbove = anchor.top - gap - qpH >= pad

        const preferredTop = () => {
            const rowAligned = anchor.top - 8
            const topToFit = vh - pad - qpH
            return Math.min(rowAligned, topToFit)
        }

        let left
        let top
        let dir = 'right'

        if (fitsRight) {
            left = anchor.right + gap
            top = preferredTop()
            dir = 'right'
        } else if (fitsLeft) {
            left = anchor.left - qpW - gap
            top = preferredTop()
            dir = 'left'
        } else if (fitsBelow) {
            left = Math.min(
                Math.max(pad, anchor.left),
                vw - qpW - pad,
            )
            top = anchor.bottom + gap
            dir = 'down'
        } else if (fitsAbove) {
            left = Math.min(
                Math.max(pad, anchor.left),
                vw - qpW - pad,
            )
            top = anchor.top - qpH - gap
            dir = 'up'
        } else {
            left = Math.min(
                Math.max(pad, anchor.left),
                vw - qpW - pad,
            )
            top = preferredTop()
            dir = 'right'
        }

        top = Math.min(Math.max(pad, top), vh - qpH - pad)
        left = Math.min(Math.max(pad, left), vw - qpW - pad)

        qp.style.left = `${left}px`
        qp.style.top = `${top}px`
        setDirection(dir)
    }, [anchorEl, expanded])

    useEffect(() => {
        if (!open || !anchorEl || expanded) return undefined

        positionPreview()
        const rafId = requestAnimationFrame(positionPreview)

        const node = qpRef.current
        const resizeObserver =
            typeof ResizeObserver !== 'undefined' && node
                ? new ResizeObserver(() => positionPreview())
                : null
        if (node && resizeObserver) {
            resizeObserver.observe(node)
        }

        return () => {
            cancelAnimationFrame(rafId)
            resizeObserver?.disconnect()
        }
    }, [
        open,
        anchorEl,
        task,
        expanded,
        positionPreview,
        fullPreview,
        fullPreviewLoading,
    ])

    useEffect(() => {
        const onScroll = () => {
            if (open && anchorEl && !expanded) positionPreview()
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)
        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
        }
    }, [open, anchorEl, expanded, positionPreview])

    useEffect(() => {
        if (!open) {
            setPinned(false)
            setExpanded(false)
        }
    }, [open])

    if (!task) return null

    const previewAnswers = fullPreviewError
        ? []
        : flattenFullPreviewAnswers(
              fullPreview,
              expanded ? null : DASHBOARD_TOP_ANSWER_LIMIT,
          )

    const scoreBySection = getFullPreviewScoreBySection(fullPreview)

    const progressPct = getFullPreviewProgress(fullPreview, task.ProgressPct || 0)

    const handleClose = () => {
        setPinned(false)
        setExpanded(false)
        onClose()
    }

    const handleExpand = () => {
        const next = !expanded
        setExpanded(next)
        if (next && !pinned) setPinned(true)
    }

    const normalizedTask = task ? mapDashboardTaskForActions(task) : null
    const completed = isTaskCompleted(normalizedTask)
    const taskIsReadOnly = isTaskReadOnly(normalizedTask, AuthService.getUser())
    const isDown =
        Number(normalizedTask?.Status) === StatusType.Down ||
        Number(normalizedTask?.Status) === 8 ||
        String(normalizedTask?.StatusName || '')
            .trim()
            .toLowerCase() === 'down'
    const inspectionRouteId = getTaskInspectionRouteId(normalizedTask)
    const inspectionDisplayId =
        getTaskInspectionNumericId(normalizedTask) || inspectionRouteId
    const customerDisplay = getTaskCustomerDisplay(task)

    const previewNode = (
        <QuickPreview
            ref={qpRef}
            $open={open}
            $expanded={expanded}
            className={`${expanded ? 'expanded' : ''} dir-${direction}`}
            onMouseEnter={onHoverStart}
            onMouseLeave={onHoverEnd}
        >
            <PreviewTop>
                <PreviewHeader>
                    <div>
                        <h2>{getTaskTemplateName(task) || task.Title}</h2>
                        {customerDisplay && (
                            <div className="sub">
                                {customerDisplay.label}: {customerDisplay.value}
                            </div>
                        )}
                        <div className="sub">
                            Task #{task.TaskId} · Inspection #{inspectionDisplayId}
                        </div>
                    </div>
                    <HeaderActions>
                        <SmallButton
                            type="button"
                            className={expanded ? 'expand-active' : ''}
                            onClick={handleExpand}
                        >
                            {expanded ? '« Collapse' : 'Expand »'}
                        </SmallButton>
                        <SmallButton
                            type="button"
                            $active={pinned}
                            onClick={() => setPinned(!pinned)}
                        >
                            {pinned ? 'Pinned' : 'Pin'}
                        </SmallButton>
                        <SmallButton type="button" onClick={handleClose}>
                            ×
                        </SmallButton>
                    </HeaderActions>
                </PreviewHeader>

                <PreviewMetaSection>
                    <MetaGrid>
                        <span className="k">Created By</span>
                        <span className="v">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <Avatar>{getInitials(getTaskCreatedByName(task))}</Avatar>
                                {getTaskCreatedByName(task)}
                                {taskIsReadOnly && (
                                    <ViewOnlyChip>View only</ViewOnlyChip>
                                )}
                            </span>
                        </span>
                        <span className="k">Status</span>
                        <span className="v">
                            <TaskStatusBadge
                                task={task}
                                isReportApproved={reportApproved}
                            />
                        </span>
                        <span className="k">Started</span>
                        <span className="v">{formatTaskDate(task.TaskDate || task.CreatedOn)}</span>
                        <span className="k">Due</span>
                        <span className={`v due ${getDueClass(task.DueState)}`}>
                            {formatTaskDate(task.TaskDue)}
                        </span>
                        {SHOW_TASK_DASHBOARD_SCORE && task.Score != null && (
                            <>
                                <span className="k">Score</span>
                                <span className="v">{task.Score}%</span>
                            </>
                        )}
                    </MetaGrid>
                    <ProgressRow $pct={progressPct || 0}>
                        <span>{progressPct || 0}% answered</span>
                        <div className="bar-wrap">
                            <div className="bar" />
                        </div>
                    </ProgressRow>
                </PreviewMetaSection>
            </PreviewTop>

            <PreviewScrollBody>
                <SectionLabel>
                    Top answers {fullPreviewLoading ? '(loading…)' : ''}
                </SectionLabel>
                {fullPreviewError ? (
                    <div style={{ color: '#b91c1c', fontSize: 12 }}>
                        Could not load task preview.
                    </div>
                ) : (
                    renderAnswerItems(previewAnswers)
                )}

                {SHOW_TASK_DASHBOARD_SCORE && (
                    <>
                        <SectionLabel style={{ marginTop: 12 }}>Score by section</SectionLabel>
                        {renderScoreRows(scoreBySection)}
                    </>
                )}
            </PreviewScrollBody>

            {!isDown && (
                <PreviewFooter>
                    <PreviewActions>
                        {!completed ? (
                            <button
                                type="button"
                                className="primary"
                                style={{ width: '100%', flex: 1 }}
                                onClick={async () => {
                                    try {
                                        const routeId =
                                            resolveResumeInspectionId(
                                                normalizedTask,
                                            )
                                        if (!routeId) {
                                            errorToast(
                                                'Could not open this inspection. The audit id is missing.',
                                            )
                                            return
                                        }
                                        navigate(
                                            `/app/templates/inspection/${routeId}`,
                                        )
                                    } catch {
                                        errorToast(
                                            'Could not open this inspection. Please try again.',
                                        )
                                    }
                                }}
                            >
                                {taskIsReadOnly
                                    ? 'View Inspection'
                                    : 'Resume Inspection'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="primary"
                                onClick={() => onViewReport(normalizedTask)}
                            >
                                View report
                            </button>
                        )}
                    </PreviewActions>
                </PreviewFooter>
            )}
        </QuickPreview>
    )

    return createPortal(previewNode, document.body)
}
